import { Component, Input, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { FormArray, FormBuilder, FormGroup } from '@angular/forms';
import {
    assignModelOptions,
    NotificationIdentifierModel,
    TextModel,
    TextModelOptions,
    TimesheetAndExpensesP58Model,
    TimesheetAndExpensesService,
    TimesheetNotificationModel,
} from '@evorait/pace-core';
import { faChevronDown, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { TranslateService } from '@ngx-translate/core';
import * as _ from 'lodash';

@Component({
    selector: 'pace-expenses',
    templateUrl: './expenses.component.html',
    styleUrls: ['./expenses.component.scss'],
})
export class ExpensesComponent implements OnInit, OnChanges {
    @Input()
    public model: TimesheetAndExpensesP58Model;

    @Input()
    public parentForm: FormArray;

    public expensesForm: FormGroup;
    public expensesFormChildren: FormArray;

    public expensesEntriesFormChildren: { [key: string]: FormArray } = {};

    public distance: { [key: string]: TextModel } = {};
    public notificationInfo: { [key: string]: ExpenseInfo };

    public faChevronDown = faChevronDown;
    public faChevronRight = faChevronRight;
    public constructor(
        private readonly translate: TranslateService,
        private readonly formBuilder: FormBuilder,
        private readonly timesheetAndExpensesService: TimesheetAndExpensesService,
    ) {}

    public ngOnInit(): void {
        this.expensesFormChildren = this.formBuilder.array([]);
        this.expensesForm = this.formBuilder.group({
            children: this.expensesFormChildren,
        });

        this.setNotificationInfo();

        const expensesDictionary = this.model.expensesDictionary;

        // we need to create for each entry in the expenses dictionary to be able to validate individual the entries.
        // we need to loop with a forin because the expensesDictionary is a Object.
        for (const expensesDictionaryKey in expensesDictionary) {
            if (expensesDictionary.hasOwnProperty(expensesDictionaryKey)) {
                const entryExpensesFormChildren = this.formBuilder.array([]);
                this.expensesEntriesFormChildren[expensesDictionaryKey] = entryExpensesFormChildren;
                this.expensesFormChildren.push(entryExpensesFormChildren);
            }
        }

        this.parentForm.push(this.expensesForm);
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (changes.model && !changes.model.isFirstChange()) {
            this.setNotificationInfo();
        }
    }

    private setNotificationInfo(): void {
        this.notificationInfo = _.mapValues(this.model.expensesDictionary, (expenses, notificationIdString) => {
            // create proper NotificationIdentifierModels from the strings used as indices in the expenses dictionary
            const id = NotificationIdentifierModel.fromString(notificationIdString);
            const timesheetNotification = this.getTimesheetNotificationModel(id);
            // get the saved expenses list from the submitted notification.
            if (this.model.readonly && timesheetNotification && timesheetNotification.expenses.length > 0) {
                expenses.value = timesheetNotification.expenses;
            }

            // Distance values from confirmation screen for a notification would be readonly
            if (id.qmNum) {
                this.distance[notificationIdString] = new TextModel(
                    id.toString(),
                    timesheetNotification && timesheetNotification.distance
                        ? timesheetNotification.distance.value
                        : undefined,
                    new TextModelOptions({
                        readonly: true,
                        unit: <string>this.translate.instant('CONFIRMATION_EXPENSES_KM_LABEL'),
                    }),
                );
            } else if (this.model.readonly && timesheetNotification) {
                this.model.additionalDistance = timesheetNotification.distance;
                this.model.additionalDistance = assignModelOptions(
                    this.model.additionalDistance,
                    {
                        ...this.model.additionalDistance.options,
                        readonly: this.model.readonly,
                    },
                    this.model,
                );
            }
            // create notification title string to be displayed as accordion header
            let notificationTitle: string;
            if (id.qmNum) {
                notificationTitle = `${id.qmNum} : ${id.maNum}`;
                if (timesheetNotification && timesheetNotification.customerName) {
                    notificationTitle += ` : ${timesheetNotification.customerName}`;
                }
            } else {
                notificationTitle = <string>this.translate.instant('TIMESHEET_EXPENSES_ADDITIONAL');
            }
            return {
                notification: id,
                title: notificationTitle,
                isOpen: false,
            };
        });
    }

    private getTimesheetNotificationModel(id: NotificationIdentifierModel): TimesheetNotificationModel | undefined {
        const timesheet = this.model.readonly
            ? this.timesheetAndExpensesService.getSubmittedTimesheet()
            : this.timesheetAndExpensesService.getCurrentTimesheet();

        return this.timesheetAndExpensesService.getTimesheetNotification(id, timesheet);
    }
}

interface ExpenseInfo {
    notification: NotificationIdentifierModel;
    title: string;
    isOpen: boolean;
}
